if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Check for Gruns-style gallery
        this.grunsViewport = this.querySelector('.pdp-image-carousel__viewport');
        if (this.grunsViewport) {
          this.initGrunsGallery();
          return;
        }

        if (!this.elements.thumbnails || !this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initGrunsGallery() {
        this.slides = Array.from(this.querySelectorAll('.pdp-image-carousel__slide'));
        this.thumbsNav = this.querySelector('.gallery-thumbs-nav');
        this.thumbs = Array.from(this.querySelectorAll('.gallery-nav-slide'));
        this.prevBtn = this.querySelector('.gallery-arrow--prev');
        this.nextBtn = this.querySelector('.gallery-arrow--next');

        if (!this.grunsViewport || this.slides.length === 0) return;

        this.currentIndex = 0;
        this.totalSlides = this.slides.length;

        this.prevBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.currentIndex > 0) this.scrollToSlide(this.currentIndex - 1);
        });

        this.nextBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.currentIndex < this.totalSlides - 1) this.scrollToSlide(this.currentIndex + 1);
        });

        this.thumbs.forEach((thumb, i) => {
          thumb.addEventListener('click', (e) => {
            e.preventDefault();
            this.scrollToSlide(i);
          });
        });

        let scrollTimer;
        this.grunsViewport.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const scrollLeft = this.grunsViewport.scrollLeft;
            const width = this.grunsViewport.clientWidth;
            if (width > 0) {
              const newIndex = Math.round(scrollLeft / width);
              if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.totalSlides) {
                this.currentIndex = newIndex;
                this.updateGrunsUI();
              }
            }
          }, 50);
        }, { passive: true });

        this.updateGrunsUI();
      }

      scrollToSlide(index) {
        if (index < 0 || index >= this.totalSlides) return;
        this.currentIndex = index;
        const targetSlide = this.slides[index];
        if (targetSlide) {
          this.grunsViewport.scrollTo({
            left: targetSlide.offsetLeft,
            behavior: 'smooth'
          });
        }
        this.updateGrunsUI();
      }

      updateGrunsUI() {
        this.thumbs.forEach((thumb, i) => {
          const isSelected = (i === this.currentIndex);
          thumb.classList.toggle('selected', isSelected);
          thumb.setAttribute('aria-current', isSelected ? 'true' : 'false');
          if (isSelected && this.thumbsNav) {
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        });

        if (this.prevBtn) {
          if (this.currentIndex <= 0) {
            this.prevBtn.setAttribute('disabled', 'disabled');
          } else {
            this.prevBtn.removeAttribute('disabled');
          }
        }
        if (this.nextBtn) {
          if (this.currentIndex >= this.totalSlides - 1) {
            this.nextBtn.setAttribute('disabled', 'disabled');
          } else {
            this.nextBtn.removeAttribute('disabled');
          }
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId) {
        if (this.grunsViewport && this.slides) {
          const idx = this.slides.findIndex(s => s.dataset.mediaId === mediaId || s.id.endsWith(mediaId));
          if (idx !== -1) {
            this.scrollToSlide(idx);
          }
          return;
        }

        if (!this.elements.viewer) return;
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

// Nutrition Label Drawer Handler
function initNutritionDrawer() {
  const drawer = document.getElementById('nutrition-label-drawer');
  if (!drawer) return;

  // Move drawer directly into document.body to escape any parent stacking contexts
  if (drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }

  const openDrawer = () => {
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    // Force reflow
    void drawer.offsetHeight;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!drawer.classList.contains('is-open')) {
        drawer.setAttribute('hidden', '');
      }
    }, 280);
  };

  // Delegated click handling to ensure it works reliably
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('#view-nutrition-label-btn, [data-nld-trigger], #view-nutrition-label-new');
    if (trigger) {
      e.preventDefault();
      openDrawer();
      return;
    }

    const closeBtn = e.target.closest('[data-nld-close], .nld__close, .nld__backdrop');
    if (closeBtn) {
      e.preventDefault();
      closeDrawer();
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNutritionDrawer);
} else {
  initNutritionDrawer();
}
